import {
  FileText,
  Percent,
  Clock,
  FileSignature,
  Eye,
  ReceiptText,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";

import { supplierAuthService } from '@/features/proveedores/api/supplierAuthService';
import {
  getProfileDashboard,
  type ProfileDashboardData,
} from '@/features/proveedores/api/supplierProfile.service';
import toast from "react-hot-toast";
import { formatSupplierStatus } from '@/enum/supplier';
import { ProgressBar } from '@/shared/components';
import { useNavigate } from "react-router-dom";
import { useAuth } from '@/shared/hooks/useAuth';
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { useRevelado } from "@/shared/hooks/useRevelado";
import { EsqueletoLinea } from "@/shared/components/Esqueleto";

const SupplierDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const supplierData = supplierAuthService.getSupplierData();
  const [isLoading, setIsLoading] = useState(false);
  // A-06: cada tarjeta se revela al entrar en pantalla.
  const tarjeta1 = useRevelado<HTMLDivElement>();
  const tarjeta2 = useRevelado<HTMLDivElement>();
  const tarjeta3 = useRevelado<HTMLDivElement>();
  const [dataDashboard, setDataDashboard] = useState<ProfileDashboardData>();

  const quickActions = (() => {
    const status = dataDashboard?.status;

    if (status === "approved") {
      return [
        {
          key: "view-orders",
          title: "Ver órdenes de compra",
          description: "Consulta tus órdenes disponibles y su estatus.",
          icon: ReceiptText,
          onClick: () => navigate("/proveedores/ordenes"),
        },
        {
          key: "upload-invoice",
          title: "Subir factura",
          description:
            "Ingresa a tus órdenes para cargar factura y documentos fiscales.",
          icon: Upload,
          onClick: () => navigate("/proveedores/ordenes"),
        },
      ];
    }

    if (
      status === "sent_to_review" ||
      status === "pending_purchases" ||
      status === "pending_controlling"
    ) {
      return [
        {
          key: "view-file",
          title: "Ver mi expediente",
          description:
            "Consulta el estado de revisión y seguimiento de tu expediente.",
          icon: Eye,
          onClick: () => navigate("/proveedores/expediente"),
        },
      ];
    }

    // Draft / corrección (pre-registrado, rechazado, etc.)
    return [
      {
        key: "continue-file",
        title: "Continúa llenando tu expediente",
        description: "Retoma tu expediente desde donde te quedaste.",
        icon: FileSignature,
        onClick: () => navigate("/proveedores/expediente"),
      },
    ];
  })();

  function formatSpanishDate(value: string | number | Date): string {
    const date = new Date(value);
    const formatted = new Intl.DateTimeFormat("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  const getSupplierData = async () => {
    try {
      setIsLoading(true);
      const res = await getProfileDashboard();

      if (res?.completionPercentage) {
        setDataDashboard(res);
      } else {
        toast.error(
          res?.message ??
          "Lo sentimos por el momento no es posible cargar la informacion, intenta de nuevo mas tarde",
        );
      }
    } catch {
      // El catch estaba vacio: si la peticion fallaba, la pantalla se quedaba
      // en blanco sin decir nada.
      toast.error(
        "Lo sentimos por el momento no es posible cargar la informacion, intenta de nuevo mas tarde",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSupplierData();
  }, []);

  if (
    !supplierData &&
    userHasPermission(
      "proveedores.inicio.dashboard.consultar",
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  // A-01: silueta con la forma de la pantalla, tres tarjetas y el panel de
  // acciones rapidas.
  if (isLoading) {
    return (
      <div className="p-6 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-4"
            >
              <EsqueletoLinea className="w-2/3" />
              <EsqueletoLinea className="w-1/2 h-6" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-4">
          <EsqueletoLinea className="w-40 h-5" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <EsqueletoLinea key={i} className="w-full h-12" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Panel de control de proveedores</p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Estatus del expediente */}
        <div
          ref={tarjeta1.ref}
          style={tarjeta1.style}
          className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${tarjeta1.className}`}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-gray-600 text-sm font-medium">
              Estatus del expediente
            </h3>
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: "#e1f3fc" }}
            >
              <FileText className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {dataDashboard?.status
              ? formatSupplierStatus(dataDashboard?.status)
              : ""}
          </p>
        </div>

        {/* Avance del expediente */}
        <div
          ref={tarjeta2.ref}
          style={tarjeta2.style}
          className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${tarjeta2.className}`}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-gray-600 text-sm font-medium">
              Avance del expediente
            </h3>
            <div className="bg-indigo-50 p-2 rounded-lg">
              <Percent className="w-6 h-6 text-indigo-500" />
            </div>
          </div>
          <div className="space-y-3">
            <ProgressBar
              showLabel
              size="lg"
              className="flex-col !items-start"
              textClassName="!text-[25px] !font-bold"
              variant="purple"
              label={`${dataDashboard?.completionPercentage
                ? dataDashboard.completionPercentage
                : 0
                }%`}
              value={
                dataDashboard?.completionPercentage
                  ? dataDashboard.completionPercentage
                  : 0
              }
            />
          </div>
        </div>

        {/* Última actualización */}
        <div
          ref={tarjeta3.ref}
          style={tarjeta3.style}
          className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${tarjeta3.className}`}
        >
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-gray-600 text-sm font-medium">
              Última actualización
            </h3>
            <div className="bg-cyan-50 p-2 rounded-lg">
              <Clock className="w-6 h-6 text-cyan-500" />
            </div>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {dataDashboard?.lastUpdate
              ? formatSpanishDate(dataDashboard?.lastUpdate)
              : ""}
          </p>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">
          Acciones rápidas
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <div
                key={action.key}
                className="bg-white rounded-lg p-4 border border-gray-200 cursor-pointer hover:shadow transition-all duration-200"
                onClick={action.onClick}
              >
                <div className="flex items-start space-x-4">
                  <div
                    className="p-2.5 rounded-lg flex-shrink-0"
                    style={{ backgroundColor: "#F2F7FD" }}
                  >
                    <Icon className="w-6 h-6" color="#305eba" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-gray-900 mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {action.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;
